import { createEffect, createMemo, createSignal, Show } from "solid-js";
import { Dynamic } from "solid-js/web";
import { fromActorRef } from "@fleet/state-machines/solid";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { createExtendedPowerSyncStatus, createPowerSyncQuery } from "@custodia/shared/solid";
import { cn, getUploadQueueStatsQuery, queryKeys, VISITOR_ID } from "@custodia/shared";
import { getConnectivityUI } from "@fleet/powersync";
import { ConnectivityModal } from "./connectivity-modal";
import { sessionActor } from "@/lib/session-machine";
import { useDrizzle } from "@/hooks/use-drizzle";

interface SyncIndicatorProps {
  color?: string;
  disablePress?: boolean;
  class?: string;
}

export function SyncIndicator(props: SyncIndicatorProps) {
  const { db, connectivityRef } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const snapshot = fromActorRef(connectivityRef);
  const sessionSnapshot = fromActorRef(sessionActor);
  const [modalVisible, setModalVisible] = createSignal(false);

  const userId = () => sessionSnapshot().context.session?.user.id ?? VISITOR_ID;

  // Stats query - reactive to ps_crud table
  const syncStats = createPowerSyncQuery<{ count: number }>({
    queryKey: () => queryKeys.powersync.uploadStats(userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      if (!dbInstance || userId() === VISITOR_ID) {
        return null;
      }

      return getUploadQueueStatsQuery(dbInstance);
    },
    // The query is reactive to ps_crud by default in createPowerSyncQuery
  });

  const uploadQueueSize = () => {
    const { data } = syncStats;
    // Defensive check to satisfy linters and handle loading/empty states
    if (data && Array.isArray(data) && data.length > 0) {
      return data[0]?.count ?? 0;
    }
    return 0;
  };

  const statusSnapshot = createExtendedPowerSyncStatus(() => db());
  const isUploading = () => uploadQueueSize() > 0 && statusSnapshot().isConnected;

  // Sync to machine context for unified UI computation
  createEffect(() => {
    const uploading = isUploading();
    const size = uploadQueueSize();

    // We use untrack to avoid dependency on connectivityRef if it's not supposed to be reactive here
    // although in Solid it's usually fine.
    connectivityRef.send({
      type: "SYNC_STATUS_CHANGED",
      isUploading: uploading,
      uploadQueueSize: size,
    });
  });

  // Compute final UI
  const ui = createMemo(() => {
    const base = getConnectivityUI(snapshot().value, snapshot().context);

    // Default dot logic based on state
    let showDot = true;
    let dotColorClass =
      base.colorToken === "default" ? "bg-muted-foreground" : base.bgClass.replace("/20", "");

    if (
      snapshot().matches("connected") &&
      !snapshot().context.isUploading &&
      snapshot().context.uploadQueueSize === 0
    ) {
      showDot = false;
      dotColorClass = "bg-success";
    }

    // Map color tokens to tailwind classes
    const colorClass = props.color ?? base.textClass;

    return {
      ...base,
      showDot,
      dotColorClass,
      colorClass,
    };
  });

  const handlePress = () => {
    if (props.disablePress) {
      return;
    }
    setModalVisible(true);
  };

  return (
    <div class={cn("flex items-center gap-2", props.class)}>
      <button
        type="button"
        onClick={handlePress}
        disabled={props.disablePress}
        class={cn(
          "relative flex items-center justify-center p-1 rounded-full transition-opacity active:opacity-70",
          props.disablePress ? "cursor-default" : "cursor-pointer",
        )}
      >
        <div
          class={cn(
            "flex items-center justify-center transition-transform duration-200",
            ui().spin && "animate-spin",
            ui().colorClass,
          )}
        >
          <Dynamic component={ui().icon} class="w-5 h-5" />
        </div>

        <Show when={ui().showDot}>
          <div
            class={cn(
              "absolute top-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-background",
              ui().dotColorClass,
            )}
          />
        </Show>
      </button>

      <Show when={uploadQueueSize() > 0}>
        <div class="ml-0.5">
          <span class={cn("text-[10px] font-bold leading-none tabular-nums", ui().colorClass)}>
            {uploadQueueSize()}
          </span>
        </div>
      </Show>

      <ConnectivityModal isOpen={modalVisible()} onClose={() => setModalVisible(false)} />
    </div>
  );
}
